/**
 * wc-modal.js — Modal shell: open/close/state-machine/focus-trap/backdrop
 *
 * Extracted from assets/wolden-customize.js (Sub-project B, Task B.3.1).
 * Monolith source lines: 1221-1223 (getModal), 1292-1334 (focus trap),
 * 2525-2573 (setModalState / openModal / closeModal), 8022-8029 (ESC handler).
 *
 * Public surface:
 *   export function getModal()
 *   export function getModalState()
 *   export function setModalState(state, callbacks)
 *   export function openModal(initialState, callbacks)
 *   export function closeModal(callbacks)
 *   export function initModalShell(callbacks)
 *
 * Valid data-wc-modal-state values (enum confirmed by grep in monolith):
 *   'hidden' | 'generating' | 'result' | 'timeout' | 'error'
 *   | 'quality-check' | 'email-gate' | 'daily-limit'
 *
 * Cross-cutting hooks are injected via a `callbacks` object rather than
 * imported directly, so this module stays free of circular dependencies.
 * The monolith's wolden-customize.js continues to define those hooks
 * until the remaining Sub-project B modules are extracted.
 *
 * Callbacks shape (all optional):
 *   {
 *     onClose:                 () => void,   // called when transitioning TO 'hidden'
 *     onOpen:                  () => void,   // called when transitioning FROM 'hidden'
 *     stopResolveRecovery:     () => void,   // stop resolve-recovery polling on close
 *     refreshReopenPill:       () => void,   // refresh the "View your art" pill
 *     stopLoadingTicker:       () => void,   // stop elapsed-time ticker on close
 *     flushDeferredSwap:       () => void,   // flush deferred PDP swap on modal close
 *     minimizeModalToPill:     () => void,   // minimize-while-generating animation
 *   }
 *
 * @module wc-modal
 */

import { log } from './wc2-analytics.js';

// ─── Modal root ───────────────────────────────────────────────────────────────

/**
 * Return the modal root element (`[data-wc-component="modal"]`).
 * Matches the monolith's `getModal()` at line 1221.
 *
 * @returns {Element|null}
 */
export function getModal() {
  return document.querySelector('[data-wc-component="modal"]');
}

// ─── State read ───────────────────────────────────────────────────────────────

/**
 * Read the current modal state from the DOM attribute.
 * The monolith had no standalone `getModalState()` — callers read the
 * attribute directly. This helper is new but purely additive (no side effects).
 *
 * @returns {string|null} — one of the valid state values, or null if no modal
 */
export function getModalState() {
  var modal = getModal();
  return modal ? modal.getAttribute('data-wc-modal-state') : null;
}

// ─── Focus trap ───────────────────────────────────────────────────────────────
// Phase 10 — keeps Tab / Shift+Tab cycling inside the dialog while it is open.
// Monolith lines 1292-1334.

/** @type {Element|null} */
var _focusTrapBefore = null;

/**
 * Return all keyboard-focusable, visible descendants of `root`.
 *
 * @param {Element} root
 * @returns {Element[]}
 */
function focusableInside(root) {
  if (!root) return [];
  return Array.prototype.slice.call(root.querySelectorAll(
    'button:not([disabled]), [href], input:not([disabled]), ' +
    'select:not([disabled]), textarea:not([disabled]), ' +
    '[tabindex]:not([tabindex="-1"])'
  )).filter(function (el) { return !el.hidden && el.offsetParent !== null; });
}

/**
 * keydown listener — cycles focus within the modal on Tab / Shift+Tab.
 *
 * @param {KeyboardEvent} e
 */
function trapFocus(e) {
  if (e.key !== 'Tab') return;
  var modal = getModal();
  if (!modal || modal.hidden) return;
  var focusables = focusableInside(modal);
  if (!focusables.length) return;
  var first = focusables[0];
  var last = focusables[focusables.length - 1];
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault(); last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault(); first.focus();
  }
}

/**
 * Save the currently focused element, register the Tab trap, then move
 * focus into the modal on the next microtask.
 */
function activateFocusTrap() {
  _focusTrapBefore = document.activeElement;
  document.addEventListener('keydown', trapFocus);
  // Move focus into the modal on a microtask so any state-render finishes.
  setTimeout(function () {
    var modal = getModal();
    if (!modal) return;
    var first = focusableInside(modal)[0];
    if (first) first.focus();
  }, 0);
}

/**
 * Remove the Tab trap listener and restore focus to the element that was
 * focused before the modal opened.
 */
function deactivateFocusTrap() {
  document.removeEventListener('keydown', trapFocus);
  if (_focusTrapBefore && _focusTrapBefore.focus) {
    try { _focusTrapBefore.focus(); } catch (e) { /* ignore */ }
  }
  _focusTrapBefore = null;
}

// ─── State machine ────────────────────────────────────────────────────────────

/**
 * Set the modal's `data-wc-modal-state` attribute and drive all associated
 * side-effects: show/hide the modal element, lock/unlock body scroll, manage
 * the focus trap, show/hide state-scoped sections, and fire optional callbacks.
 *
 * Extracted from monolith lines 2525-2549.
 *
 * @param {string} state — one of the valid state enum values
 * @param {object} [callbacks] — optional cross-cutting hooks (see module doc)
 * @returns {Element|null} — the modal element, or null if not found
 */
export function setModalState(state, callbacks) {
  var cb = callbacks || {};
  var modal = getModal();
  if (!modal) return null;

  var wasOpen = !modal.hidden;
  modal.setAttribute('data-wc-modal-state', state);

  if (state === 'hidden') {
    modal.hidden = true;
    document.body.classList.remove('wolden-customize__body--locked');
    if (wasOpen) deactivateFocusTrap();
    // Stop browser-side resolve-recovery polling when the modal closes.
    // (It also self-stops if all variants resolve.)
    if (typeof cb.stopResolveRecovery === 'function') cb.stopResolveRecovery();
    if (typeof cb.onClose === 'function') cb.onClose();
  } else {
    modal.hidden = false;
    document.body.classList.add('wolden-customize__body--locked');
    if (!wasOpen) activateFocusTrap();
    if (typeof cb.onOpen === 'function') cb.onOpen();
  }

  // Toggle visibility of state-scoped section divs.
  var sections = modal.querySelectorAll('[data-wc-modal-section]');
  sections.forEach(function (sec) {
    sec.hidden = sec.getAttribute('data-wc-modal-section') !== state;
  });

  // 2026-06-10 — Keep the dialog's accessible name in sync with the visible
  // state. The static aria-labelledby pointed at the loading <h2>, so after
  // the reveal rendered the dialog still announced the last loader phase
  // (e.g. "Finalizing"). For 'generating' the live loader title IS the right
  // name (it tracks the phase); for every other state, name the dialog from
  // the active section's visible heading (aria-label only wins once
  // aria-labelledby is removed, hence the attribute swap).
  // 2026-07-02 QA round-3 (round-1 #9) — extracted into syncModalAriaName so
  // reveal renderers can re-assert the name AFTER their async DOM writes
  // (showResult flips state, yields to rAF, then chunks its render — a name
  // computed at flip time can predate the headings the chunks add/change,
  // leaving the dialog announcing the last loader phase, e.g. "Almost
  // ready"). Also: only VISIBLE headings count now — a heading inside a
  // [hidden] subtree (nested sub-dialogs, inert branches) must never name
  // the dialog.
  syncModalAriaName(modal, state);

  if (typeof cb.refreshReopenPill === 'function') cb.refreshReopenPill();

  return modal;
}

/**
 * Recompute the dialog's accessible name from its current (or given) state.
 * Safe to call any time after a state change or after async render work —
 * idempotent, non-fatal. Exported so wc77-result.js can re-sync at the end
 * of showResult's chunked render (QA round-1 #9).
 *
 * @param {Element} [modalEl] — defaults to getModal()
 * @param {string}  [state]   — defaults to the modal's data-wc-modal-state
 */
export function syncModalAriaName(modalEl, state) {
  try {
    var modal = modalEl || getModal();
    if (!modal) return;
    var st = state || modal.getAttribute('data-wc-modal-state');
    if (!st) return;
    if (st === 'generating') {
      // The live loader <h2> IS the right name while generating (it tracks
      // the ticker phase). The ticker only ever writes into that h2; once
      // the state leaves 'generating' the labelledby link below is removed,
      // so a still-running ticker can no longer leak loader copy ("Almost
      // ready") into the dialog name.
      modal.setAttribute('aria-labelledby', 'wolden-customize-modal-title');
      modal.removeAttribute('aria-label');
      return;
    }
    var activeSec = modal.querySelector('[data-wc-modal-section="' + st + '"]');
    var name = '';
    if (activeSec) {
      var headings = activeSec.querySelectorAll('h1, h2, h3');
      for (var i = 0; i < headings.length; i++) {
        // Skip hidden headings — [hidden] on the element or any ancestor
        // inside the section (e.g. an inert sub-dialog) means the customer
        // can't see it, so it must not name the dialog.
        if (headings[i].closest('[hidden]')) continue;
        var txt = headings[i].textContent && headings[i].textContent.trim();
        if (txt) { name = txt; break; }
      }
    }
    if (!name) name = (st === 'result' ? 'Customize your art' : 'Customize window');
    modal.setAttribute('aria-label', name);
    modal.removeAttribute('aria-labelledby');
  } catch (e) { /* accessible-name sync is non-fatal */ }
}

// ─── Open / Close convenience wrappers ───────────────────────────────────────

/**
 * Open the modal in the given state (defaults to `'generating'`).
 * Matches monolith lines 2551-2553.
 *
 * @param {string} [initialState='generating']
 * @param {object} [callbacks] — forwarded to setModalState
 */
export function openModal(initialState, callbacks) {
  setModalState(initialState || 'generating', callbacks);
}

/**
 * Close the modal.
 *
 * Special case: if the modal is currently in `'generating'` state, the close
 * is intercepted by the minimize-to-pill animation (callbacks.minimizeModalToPill)
 * instead of hiding the modal outright. This matches monolith lines 2555-2573.
 *
 * @param {object} [callbacks] — optional cross-cutting hooks (see module doc)
 */
export function closeModal(callbacks) {
  var cb = callbacks || {};
  var modal = getModal();
  var state = modal && modal.getAttribute('data-wc-modal-state');

  // Phase 16 — if the user closes during 'generating', minimize the panel
  // down into a bottom-right pill so the orchestrator fetch keeps running.
  if (state === 'generating') {
    if (typeof cb.minimizeModalToPill === 'function') {
      cb.minimizeModalToPill();
    }
    return;
  }

  setModalState('hidden', cb);

  if (typeof cb.stopLoadingTicker === 'function') cb.stopLoadingTicker();

  // Phase B — flush any deferred swap (modal-driven format/canvas-size change)
  // so the PDP behind the now-closed modal reflects the customer's final choice
  // (URL + title + gallery + description).
  if (typeof cb.flushDeferredSwap === 'function') {
    try { cb.flushDeferredSwap(); } catch (e) { log('flushDeferredSwap failed', e && e.message); }
  }
}

// ─── Document-level keyboard handler ─────────────────────────────────────────

/**
 * Register the document-level Escape key handler.
 * ESC closes the lightbox first (if open), then the modal.
 * Extracted from monolith lines 8022-8029 (inside initModal).
 *
 * @param {object} [callbacks]
 * @param {function} [callbacks.getLightbox] — returns the lightbox element
 * @param {function} [callbacks.closeLightbox] — closes the lightbox
 * @param {function} [callbacks.closeModal] — overrides the default closeModal
 *   (if omitted, calls this module's own closeModal with no extra callbacks)
 */
function bindEscapeKey(callbacks) {
  var cb = callbacks || {};
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    // Lightbox takes priority over modal.
    if (typeof cb.getLightbox === 'function') {
      var lb = cb.getLightbox();
      if (lb && !lb.hidden) {
        if (typeof cb.closeLightbox === 'function') cb.closeLightbox();
        return;
      }
    }
    var m = getModal();
    if (m && !m.hidden) {
      if (typeof cb.closeModal === 'function') {
        cb.closeModal();
      } else {
        closeModal(cb);
      }
    }
  });
}

// ─── Init ─────────────────────────────────────────────────────────────────────

/**
 * Wire modal shell event listeners once.
 *
 * Call this once from the main init path (replaces the shell portion of the
 * monolith's `initModal()` at line 7652).
 *
 * Note: the monolith's `initModal()` also wires non-shell concerns (selector
 * pills, format toggles, ATC actions, quality-check inputs, etc.) — those
 * remain in the monolith until extracted by later Sub-project B tasks.
 *
 * @param {object} [callbacks] — cross-cutting hooks (see module doc)
 *   Additional keys used only in initModalShell:
 *     getLightbox:    () => Element|null
 *     closeLightbox:  () => void
 */
export function initModalShell(callbacks) {
  var cb = callbacks || {};

  // ESC key — lightbox first, then modal.
  bindEscapeKey(cb);

  log('modal shell initialized');
}
